import { useCallback, RefObject } from 'react';

import Konva from 'konva';
import { useShallow } from 'zustand/react/shallow';

// Import Konva components
import { Line, Rect, Circle, Arrow, Text, Group } from 'react-konva';

import { useMagicPenStore, useRoomStore } from '@src/stores';
import { createDragBoundFuncWithGetter, toRelativePosition, calculateResponsiveFontSize } from '../../helpers';
import { useRoomContext } from '@src/contexts';

import {
  DEFAULT_SHAPE_SIZE,
  HIT_STROKE_WIDTH,
  LINE_TENSION,
  ARROW_POINTER_LENGTH,
  ARROW_POINTER_WIDTH,
  SELECTION_STROKE_COLOR,
  SELECTION_STROKE_WIDTH_INCREASE,
  SELECTION_SHADOW_BLUR,
  SELECTION_SHADOW_BLUR_HOVER,
  HOVER_OPACITY,
  ERASER_STROKE_COLOR,
  ERASER_HOVER_OPACITY,
  ERASER_FADE_DURATION_MS,
  STICKER_FONT_SIZE,
} from '../../magicPen.constants';

import { EMagicPenShape, ICanvasObject, IEditingStickyNotes, TSetState } from '@src/types';

type DragBoundsOverrides = {
  minX?: number;
  minY?: number;
  maxX?: number;
  maxY?: number;
};

const clampPositionToStageBounds = (
  node: Konva.Node,
  stage: Konva.Stage,
  pos: Konva.Vector2d,
  overrides: DragBoundsOverrides = {}
) => {
  const { minX = 0, minY = 0, maxX = stage.width(), maxY = stage.height() } = overrides;

  const originalPosition = node.position();
  node.position(pos);
  const clientRect = node.getClientRect({ skipShadow: true });
  node.position(originalPosition);

  const offsetX = clientRect.x - pos.x;
  const offsetY = clientRect.y - pos.y;
  const width = clientRect.width;
  const height = clientRect.height;

  const minAllowedX = minX - offsetX;
  const maxAllowedX = maxX - offsetX - width;
  const minAllowedY = minY - offsetY;
  const maxAllowedY = maxY - offsetY - height;

  const clampedX =
    minAllowedX <= maxAllowedX ? Math.max(minAllowedX, Math.min(pos.x, maxAllowedX)) : (minAllowedX + maxAllowedX) / 2;

  const clampedY =
    minAllowedY <= maxAllowedY ? Math.max(minAllowedY, Math.min(pos.y, maxAllowedY)) : (minAllowedY + maxAllowedY) / 2;

  return {
    x: clampedX,
    y: clampedY,
  };
};

const createLineDragBoundFunc = (stageRef: RefObject<Konva.Stage>) =>
  function (this: Konva.Node, pos: Konva.Vector2d) {
    const stage = stageRef.current;
    if (!stage) return pos;

    return clampPositionToStageBounds(this, stage, pos);
  };

const createRectDragBoundFunc = (stageRef: RefObject<Konva.Stage>) =>
  function (this: Konva.Node, pos: Konva.Vector2d) {
    const stage = stageRef.current;
    if (!stage) return pos;

    return clampPositionToStageBounds(this, stage, pos);
  };

export const useObjectRenderer = ({
  isEditingText,
  handleObjectClick,
  handleObjectHover,
  handleObjectUnhover,
  handleTextClick,
  handleTextDblClick,
  stageRef,
  setEditingStickyNote,
  editingStickyNote,
  setTooltipPos,
  handleEraserClick,
  currentCreatedById,
  canAnnotate,
  emitUpdateAnnotation,
}: {
  isEditingText: boolean;
  handleObjectClick: (e: Konva.KonvaEventObject<MouseEvent>, handleEraserClick?: (node: Konva.Node) => void) => void;
  handleObjectHover: (e: Konva.KonvaEventObject<MouseEvent>, objectId: string) => void;
  handleObjectUnhover: () => void;
  handleTextClick: (e: Konva.KonvaEventObject<MouseEvent>, id: string, currentText: string) => void;
  handleTextDblClick: (textNode: Konva.Text, id: string, currentText: string) => void;
  stageRef: RefObject<Konva.Stage>;
  setEditingStickyNote: TSetState<IEditingStickyNotes | null>;
  editingStickyNote: IEditingStickyNotes | null;
  setTooltipPos: TSetState<{
    x: number;
    y: number;
  } | null>;
  handleEraserClick: (node: Konva.Node) => void;
  currentCreatedById: string;
  canAnnotate: boolean;
  emitUpdateAnnotation: (objects: ICanvasObject[]) => Promise<void>;
}) => {
  const { selectedId, hoveredId, activeTool, brushColor, objects, setObjects, bringToFront, setSelectedId } =
    useMagicPenStore(
      useShallow((state) => ({
        selectedId: state.selectedId,
        hoveredId: state.hoveredId,
        activeTool: state.activeTool,
        brushColor: state.brushColor,
        objects: state.objects,
        setObjects: state.setObjects,
        bringToFront: state.bringToFront,
        setSelectedId: state.setSelectedId,
      }))
    );

  const { isHost, isCurrentPeerSharing } = useRoomContext();

  const { hasMagicPenAnnotatePermission } = useRoomStore(
    useShallow((state) => ({
      hasMagicPenAnnotatePermission: state.hasMagicPenAnnotatePermission,
    }))
  );

  // Granted attendees (non-host, non-screen-sharer) can only delete their own objects
  const isGrantedNonHostNonSharer = hasMagicPenAnnotatePermission && !isHost && !isCurrentPeerSharing;
  const canDeleteOthersObjects = !isGrantedNonHostNonSharer && canAnnotate;

  const renderObject = useCallback(
    (obj: ICanvasObject) => {
      if (!stageRef.current) return null;

      const containerWidth = stageRef.current.width();
      const containerHeight = stageRef.current.height();

      // Convert relative coordinates to absolute coordinates for rendering
      const absoluteObj = { ...obj };

      if (obj.relativeX !== undefined && obj.relativeY !== undefined) {
        absoluteObj.x = obj.relativeX * containerWidth;
        absoluteObj.y = obj.relativeY * containerHeight;
      } else if (obj.x === undefined || obj.y === undefined) {
        // If no coordinates at all, default to 0,0
        absoluteObj.x = absoluteObj.x || 0;
        absoluteObj.y = absoluteObj.y || 0;
      }

      if (obj.relativeWidth !== undefined && obj.relativeHeight !== undefined) {
        absoluteObj.width = obj.relativeWidth * containerWidth;
        absoluteObj.height = obj.relativeHeight * containerHeight;
      }

      // Handle line points conversion
      if (obj.relativePoints && obj.relativePoints.length > 0) {
        absoluteObj.points = obj.relativePoints.map((point: number, index: number) => {
          if (index % 2 === 0) {
            // X coordinate
            return point * containerWidth;
          } else {
            // Y coordinate
            return point * containerHeight;
          }
        });
      } else if (obj.points && obj.points.length > 0 && (!obj.relativePoints || obj.relativePoints.length === 0)) {
        // If we have absolute points but no relative points, use the absolute points directly
        absoluteObj.points = obj.points;
      }

      const isCreatedByMe = absoluteObj.createdBy?.peer_id === currentCreatedById;

      const isSelected = selectedId === absoluteObj.id;
      const isHovered = hoveredId === absoluteObj.id && !isSelected;
      const isSelectMode = activeTool === 'select';
      const isViewerMode = activeTool === 'none';
      const isEraserMode = activeTool === 'eraser';
      const isDeleting = !!absoluteObj.deletedAt;

      // Check if user can delete this specific object
      const canDeleteThisObject = isCreatedByMe || canDeleteOthersObjects;

      // Computed interaction flags
      const canInteract = canAnnotate && isCreatedByMe;
      const canDrag =
        canInteract &&
        !isDeleting &&
        (isSelectMode || (obj.type === EMagicPenShape.STICKY_NOTE && activeTool === 'sticky'));
      const canEditText = canInteract && !isEditingText && (isSelectMode || activeTool === 'text');

      let fadeOpacity = 1;
      if (isDeleting) {
        const age = Date.now() - absoluteObj.deletedAt!;
        fadeOpacity = Math.max(0, 1 - age / ERASER_FADE_DURATION_MS);
      }

      // Apply ephemeral pen opacity if it exists
      const ephemeralOpacity = absoluteObj.opacity !== undefined ? absoluteObj.opacity : 1;
      const finalOpacity = fadeOpacity * ephemeralOpacity;

      const selectionProps = isSelected
        ? {
            stroke: SELECTION_STROKE_COLOR,
            strokeWidth: (absoluteObj.strokeWidth || 3) + SELECTION_STROKE_WIDTH_INCREASE,
            shadowColor: SELECTION_STROKE_COLOR,
            shadowBlur: SELECTION_SHADOW_BLUR,
            shadowOffset: { x: 0, y: 0 },
            opacity: finalOpacity,
          }
        : isHovered && isEraserMode && canDeleteThisObject
          ? {
              stroke: ERASER_STROKE_COLOR,
              strokeWidth: (absoluteObj.strokeWidth || 3) + SELECTION_STROKE_WIDTH_INCREASE,
              shadowColor: ERASER_STROKE_COLOR,
              shadowBlur: SELECTION_SHADOW_BLUR_HOVER + 3,
              shadowOffset: { x: 0, y: 0 },
              opacity: ERASER_HOVER_OPACITY * finalOpacity,
            }
          : isHovered
            ? {
                stroke: absoluteObj.stroke,
                strokeWidth: absoluteObj.strokeWidth,
                shadowColor: SELECTION_STROKE_COLOR,
                shadowBlur: SELECTION_SHADOW_BLUR_HOVER,
                shadowOffset: { x: 0, y: 0 },
                opacity: HOVER_OPACITY * finalOpacity,
              }
            : {
                opacity: finalOpacity,
              };

      switch (absoluteObj.type) {
        case EMagicPenShape.LINE: {
          // Validate line has points
          if (!absoluteObj.points || absoluteObj.points.length < 2) return null;

          return (
            <Line
              key={absoluteObj.id}
              id={absoluteObj.id}
              x={absoluteObj.x || 0}
              y={absoluteObj.y || 0}
              rotation={absoluteObj.rotation ?? 0}
              offsetX={0}
              offsetY={0}
              points={absoluteObj.points}
              stroke={absoluteObj.stroke}
              strokeWidth={absoluteObj.strokeWidth}
              tension={LINE_TENSION}
              lineCap="round"
              lineJoin="round"
              hitStrokeWidth={HIT_STROKE_WIDTH}
              draggable={canDrag}
              listening={(isSelectMode || isEraserMode || isViewerMode) && !isDeleting}
              dragBoundFunc={stageRef.current ? createLineDragBoundFunc(stageRef) : undefined}
              onClick={(e) => handleObjectClick(e, handleEraserClick)}
              onMouseEnter={(e) => handleObjectHover(e, absoluteObj.id)}
              onMouseLeave={handleObjectUnhover}
              onDragStart={() => {
                setTooltipPos(null);
              }}
              onDragEnd={(e) => {
                // Update line position in the store
                const newX = e.target.x();
                const newY = e.target.y();
                // Convert absolute coordinates to relative coordinates
                const relativePos = toRelativePosition(newX, newY, containerWidth, containerHeight);

                const updatedObjects = objects.map((o: ICanvasObject) =>
                  o.id === obj.id
                    ? {
                        ...o,
                        x: newX,
                        y: newY,
                        relativeX: relativePos.x,
                        relativeY: relativePos.y,
                        relativePoints: absoluteObj.relativePoints,
                      }
                    : o
                );
                setObjects(updatedObjects);

                const updatedObject = updatedObjects.find((o) => o.id === obj.id);
                if (updatedObject && isCreatedByMe) {
                  void emitUpdateAnnotation([updatedObject]);
                }
              }}
              {...selectionProps}
            />
          );
        }
        case EMagicPenShape.RECTANGLE:
          return (
            <Rect
              key={absoluteObj.id}
              id={absoluteObj.id}
              x={absoluteObj.x || 0}
              y={absoluteObj.y || 0}
              rotation={absoluteObj.rotation ?? 0}
              width={absoluteObj.width || DEFAULT_SHAPE_SIZE}
              height={absoluteObj.height || DEFAULT_SHAPE_SIZE}
              stroke={absoluteObj.stroke}
              strokeWidth={absoluteObj.strokeWidth}
              strokeScaleEnabled={false}
              fillEnabled={false}
              hitStrokeWidth={HIT_STROKE_WIDTH}
              draggable={canDrag}
              listening={(isSelectMode || isEraserMode || isViewerMode) && !isDeleting}
              dragBoundFunc={stageRef.current ? createRectDragBoundFunc(stageRef) : undefined}
              onClick={(e) => handleObjectClick(e, handleEraserClick)}
              onMouseEnter={(e) => handleObjectHover(e, absoluteObj.id)}
              onMouseLeave={handleObjectUnhover}
              onDragStart={() => {
                setTooltipPos(null);
              }}
              onDragEnd={(e) => {
                // Update rectangle position in the store
                const newX = e.target.x();
                const newY = e.target.y();
                // Convert absolute coordinates to relative coordinates
                const relativePos = toRelativePosition(newX, newY, containerWidth, containerHeight);

                const updatedObjects = objects.map((o: ICanvasObject) =>
                  o.id === absoluteObj.id
                    ? { ...o, x: newX, y: newY, relativeX: relativePos.x, relativeY: relativePos.y }
                    : o
                );

                setObjects(updatedObjects);

                const updatedObject = updatedObjects.find((o) => o.id === obj.id);
                if (updatedObject && isCreatedByMe) {
                  void emitUpdateAnnotation([updatedObject]);
                }
              }}
              {...selectionProps}
            />
          );
        case EMagicPenShape.CIRCLE: {
          const width = absoluteObj.width || DEFAULT_SHAPE_SIZE;
          const height = absoluteObj.height || DEFAULT_SHAPE_SIZE;
          const radius = Math.min(Math.abs(width), Math.abs(height)) / 2;

          return (
            <Circle
              key={absoluteObj.id}
              id={absoluteObj.id}
              x={absoluteObj.x || 0}
              y={absoluteObj.y || 0}
              rotation={absoluteObj.rotation ?? 0}
              radius={radius}
              stroke={absoluteObj.stroke}
              strokeWidth={absoluteObj.strokeWidth}
              strokeScaleEnabled={false}
              fillEnabled={false}
              hitStrokeWidth={HIT_STROKE_WIDTH}
              draggable={canDrag}
              listening={(isSelectMode || isEraserMode || isViewerMode) && !isDeleting}
              dragBoundFunc={
                stageRef.current
                  ? function (pos) {
                      const stage = stageRef.current;
                      if (!stage) return pos;

                      const stageWidth = stage.width();
                      const stageHeight = stage.height();

                      // Use actual dimensions instead of getClientRect to avoid transformer padding
                      const circleNode = this as unknown as Konva.Circle;
                      const radius = circleNode.radius() * Math.max(circleNode.scaleX(), circleNode.scaleY());
                      const strokeWidth = circleNode.strokeWidth() || 0;

                      // For circles, (x, y) is the center position
                      // Constrain center so entire circle (including stroke) stays within canvas
                      // Left edge: x - radius - strokeWidth/2 >= 0, so x >= radius + strokeWidth/2
                      // Right edge: x + radius + strokeWidth/2 <= stageWidth, so x <= stageWidth - radius - strokeWidth/2
                      const minX = radius + strokeWidth / 2;
                      const maxX = stageWidth - radius - strokeWidth / 2;
                      const minY = radius + strokeWidth / 2;
                      const maxY = stageHeight - radius - strokeWidth / 2;

                      const newX = Math.max(minX, Math.min(pos.x, maxX));
                      const newY = Math.max(minY, Math.min(pos.y, maxY));

                      return { x: newX, y: newY };
                    }
                  : undefined
              }
              onClick={(e) => handleObjectClick(e, handleEraserClick)}
              onMouseEnter={(e) => handleObjectHover(e, absoluteObj.id)}
              onMouseLeave={handleObjectUnhover}
              onDragStart={() => {
                setTooltipPos(null);
              }}
              onDragEnd={(e) => {
                // For circles, we need to store the center position
                const newX = e.target.x();
                const newY = e.target.y();
                // Convert absolute coordinates to relative coordinates
                const relativePos = toRelativePosition(newX, newY, containerWidth, containerHeight);
                const updatedObjects = objects.map((o: ICanvasObject) =>
                  o.id === absoluteObj.id
                    ? { ...o, x: newX, y: newY, relativeX: relativePos.x, relativeY: relativePos.y }
                    : o
                );
                setObjects(updatedObjects);

                const updatedObject = updatedObjects.find((o) => o.id === obj.id);
                if (updatedObject && isCreatedByMe) {
                  void emitUpdateAnnotation([updatedObject]);
                }
              }}
              {...selectionProps}
            />
          );
        }
        case EMagicPenShape.ARROW: {
          // Define arrow points for rendering
          const arrowPoints = [0, 0, absoluteObj.width || DEFAULT_SHAPE_SIZE, absoluteObj.height || 0];

          return (
            <Arrow
              key={absoluteObj.id}
              id={absoluteObj.id}
              x={absoluteObj.x || 0}
              y={absoluteObj.y || 0}
              rotation={absoluteObj.rotation ?? 0}
              points={arrowPoints}
              stroke={absoluteObj.stroke}
              strokeWidth={absoluteObj.strokeWidth}
              strokeScaleEnabled={false}
              fill={absoluteObj.stroke}
              pointerLength={ARROW_POINTER_LENGTH}
              pointerWidth={ARROW_POINTER_WIDTH}
              lineCap="round"
              lineJoin="round"
              hitStrokeWidth={HIT_STROKE_WIDTH}
              draggable={canDrag}
              listening={(isSelectMode || isEraserMode || isViewerMode) && !isDeleting}
              dragBoundFunc={
                stageRef.current
                  ? createDragBoundFuncWithGetter(() => {
                      // Get current dimensions from store (handles resized objects)
                      const currentObj = objects.find((o) => o.id === absoluteObj.id);
                      return {
                        width: currentObj?.width || absoluteObj.width || DEFAULT_SHAPE_SIZE,
                        height: currentObj?.height || absoluteObj.height || DEFAULT_SHAPE_SIZE,
                      };
                    }, stageRef)
                  : undefined
              }
              onClick={(e) => handleObjectClick(e, handleEraserClick)}
              onMouseEnter={(e) => handleObjectHover(e, absoluteObj.id)}
              onMouseLeave={handleObjectUnhover}
              onDragStart={() => {
                setTooltipPos(null);
              }}
              onDragEnd={(e) => {
                const newX = e.target.x();
                const newY = e.target.y();
                // Convert absolute coordinates to relative coordinates
                const relativePos = toRelativePosition(newX, newY, containerWidth, containerHeight);
                const updatedObjects = objects.map((o: ICanvasObject) =>
                  o.id === absoluteObj.id
                    ? { ...o, x: newX, y: newY, relativeX: relativePos.x, relativeY: relativePos.y }
                    : o
                );

                setObjects(updatedObjects);

                const updatedObject = updatedObjects.find((o) => o.id === obj.id);
                if (updatedObject && isCreatedByMe) {
                  void emitUpdateAnnotation([updatedObject]);
                }
              }}
              {...selectionProps}
            />
          );
        }
        case EMagicPenShape.STICKER: {
          // Stickers don't get selection styling - only opacity
          const stickerProps = {
            opacity: finalOpacity,
          };

          // Calculate responsive font size based on container dimensions
          const baseFontSize = absoluteObj.fontSize ?? STICKER_FONT_SIZE;
          const responsiveFontSize = calculateResponsiveFontSize(baseFontSize, containerWidth, containerHeight);

          return (
            <Text
              key={absoluteObj.id}
              id={absoluteObj.id}
              x={absoluteObj.x || 0}
              y={absoluteObj.y || 0}
              text={absoluteObj.content}
              fontSize={responsiveFontSize}
              rotation={absoluteObj.rotation ?? 0}
              // added scaleX and scaleY to the props to handle the scale of the sticker upon resizing
              scaleX={absoluteObj.scaleX ?? 1}
              scaleY={absoluteObj.scaleY ?? 1}
              draggable={canDrag}
              listening={(isSelectMode || isEraserMode || isViewerMode) && !isDeleting}
              dragBoundFunc={
                stageRef.current
                  ? function (this: Konva.Node, pos: Konva.Vector2d) {
                      const stage = stageRef.current;
                      if (!stage) return pos;
                      const textNode = this as Konva.Text;
                      const height = textNode.height() * textNode.scaleY();
                      const currentTopAllowance = height * 0.1;
                      return clampPositionToStageBounds(textNode, stage, pos, {
                        minY: Math.max(currentTopAllowance, 0),
                      });
                    }
                  : undefined
              }
              onClick={(e) => handleObjectClick(e, handleEraserClick)}
              onMouseEnter={(e) => handleObjectHover(e, absoluteObj.id)}
              onMouseLeave={handleObjectUnhover}
              onDragStart={() => {
                setTooltipPos(null);
              }}
              onDragEnd={(e) => {
                // Update sticker position in the store
                const newX = e.target.x();
                const newY = e.target.y();
                // Convert absolute coordinates to relative coordinates
                const relativePos = toRelativePosition(newX, newY, containerWidth, containerHeight);

                const updatedObjects = objects.map((o: ICanvasObject) =>
                  o.id === obj.id ? { ...o, x: newX, y: newY, relativeX: relativePos.x, relativeY: relativePos.y } : o
                );
                setObjects(updatedObjects);

                const updatedObject = updatedObjects.find((o) => o.id === obj.id);
                if (updatedObject && isCreatedByMe) {
                  void emitUpdateAnnotation([updatedObject]);
                }
              }}
              {...stickerProps}
            />
          );
        }
        case EMagicPenShape.TEXT_BOX:
          // Calculate responsive font size for text boxes
          const responsiveFontSize = absoluteObj.fontSize
            ? calculateResponsiveFontSize(absoluteObj.fontSize, containerWidth, containerHeight)
            : 20;

          return (
            <Text
              key={absoluteObj.id}
              id={absoluteObj.id}
              x={absoluteObj.x}
              y={absoluteObj.y}
              text={absoluteObj.text || ''}
              fontSize={responsiveFontSize}
              fill={absoluteObj.fill}
              width={absoluteObj.width}
              wrap="word"
              ellipsis={false}
              rotation={absoluteObj.rotation ?? 0}
              draggable={canEditText}
              listening={(['text', 'select'].includes(activeTool) || isEraserMode || isViewerMode) && !isDeleting}
              dragBoundFunc={
                stageRef.current
                  ? function (this: Konva.Node, pos: Konva.Vector2d) {
                      const stage = stageRef.current;
                      if (!stage) return pos;
                      return clampPositionToStageBounds(this, stage, pos);
                    }
                  : undefined
              }
              onClick={(e) => {
                if (canEditText) {
                  e.cancelBubble = true;
                  if (e.evt) {
                    e.evt.stopPropagation();
                  }
                  handleTextClick(e as Konva.KonvaEventObject<MouseEvent>, absoluteObj.id, absoluteObj.text || '');
                }
                handleObjectClick(e, handleEraserClick);
              }}
              onTap={(e) => {
                // For touch devices
                if (canEditText) {
                  e.cancelBubble = true;
                  if (e.evt) {
                    e.evt.stopPropagation();
                  }
                  handleTextClick(e as Konva.KonvaEventObject<MouseEvent>, absoluteObj.id, absoluteObj.text || '');
                }
              }}
              onDblClick={(e) => {
                if (canEditText && e.target instanceof Konva.Text) {
                  handleTextDblClick(e.target, absoluteObj.id, absoluteObj.text || '');
                }
              }}
              onMouseEnter={(e) => handleObjectHover(e, absoluteObj.id)}
              onMouseLeave={handleObjectUnhover}
              onDragStart={() => {
                setTooltipPos(null);
              }}
              onDragEnd={(e) => {
                const newX = e.target.x();
                const newY = e.target.y();
                // Convert absolute coordinates to relative coordinates
                const relativePos = toRelativePosition(newX, newY, containerWidth, containerHeight);
                const updatedObjects = objects.map((o: ICanvasObject) =>
                  o.id === obj.id ? { ...o, x: newX, y: newY, relativeX: relativePos.x, relativeY: relativePos.y } : o
                );

                setObjects(updatedObjects);

                const updatedObject = updatedObjects.find((o) => o.id === obj.id);
                if (updatedObject && isCreatedByMe) {
                  void emitUpdateAnnotation([updatedObject]);
                }
              }}
              {...selectionProps}
            />
          );
        case EMagicPenShape.STICKY_NOTE:
          // Calculate responsive font size for sticky notes
          const stickyNoteFontSize = absoluteObj.fontSize
            ? calculateResponsiveFontSize(absoluteObj.fontSize, containerWidth, containerHeight)
            : 14;
          const rectWidth = absoluteObj.width || 100;
          const rectHeight = absoluteObj.height || 60;
          const padding = 10;
          const textWidth = Math.max(rectWidth - padding * 2, 0);
          const textHeight = Math.max(rectHeight - padding * 2, 0);

          return (
            <Group
              key={absoluteObj.id}
              id={absoluteObj.id}
              x={absoluteObj.x || 0}
              y={absoluteObj.y || 0}
              rotation={absoluteObj.rotation ?? 0}
              draggable={canDrag}
              listening={true}
              onClick={(e) => handleObjectClick(e, handleEraserClick)}
              onMouseEnter={(e) => {
                handleObjectHover(e, absoluteObj.id);
                if (isCreatedByMe) {
                  const stage = e.target.getStage();
                  if (stage && stage.container) {
                    const container = stage.container();
                    if (container) {
                      container.style.cursor = 'move';
                    }
                  }
                }
              }}
              onMouseLeave={(e) => {
                handleObjectUnhover();
                const stage = e.target.getStage();
                if (stage && stage.container) {
                  const container = stage.container();
                  if (container) {
                    container.style.cursor = 'default';
                  }
                }
              }}
              dragBoundFunc={
                stageRef.current
                  ? createDragBoundFuncWithGetter(() => {
                      // Get current dimensions from store (handles resized objects)
                      const currentObj = objects.find((o) => o.id === absoluteObj.id);
                      return {
                        width: currentObj?.width || absoluteObj.width || 100,
                        height: currentObj?.height || absoluteObj.height || 60,
                      };
                    }, stageRef)
                  : undefined
              }
              onDragStart={() => {
                setTooltipPos(null);
              }}
              onDragEnd={(e) => {
                if (!isCreatedByMe) {
                  e.target.position({ x: absoluteObj.x || 0, y: absoluteObj.y || 0 });
                  return;
                }
                const newX = e.target.x();
                const newY = e.target.y();
                // Convert absolute coordinates to relative coordinates
                const relativePos = toRelativePosition(newX, newY, containerWidth, containerHeight);
                const updatedObjects = objects.map((o: ICanvasObject) =>
                  o.id === obj.id ? { ...o, x: newX, y: newY, relativeX: relativePos.x, relativeY: relativePos.y } : o
                );

                setObjects(updatedObjects);

                const updatedObject = updatedObjects.find((o) => o.id === obj.id);
                if (updatedObject && isCreatedByMe) {
                  void emitUpdateAnnotation([updatedObject]);
                }
              }}
              {...selectionProps}
            >
              <Rect
                width={rectWidth}
                height={rectHeight}
                fill={absoluteObj.backgroundColor || brushColor}
                stroke={selectedId === absoluteObj.id ? '#007bff' : 'transparent'}
                strokeWidth={selectedId === absoluteObj.id ? 2 : 0}
                cornerRadius={4}
                shadowColor="black"
                shadowBlur={4}
                shadowOffsetX={2}
                shadowOffsetY={2}
                shadowOpacity={0.3}
                onClick={(e) => {
                  // Allow selection when select tool OR sticky tool is active
                  if (!canInteract) return;

                  // Click on background - just select, don't enter editing mode
                  e.cancelBubble = true; // Prevent event from bubbling to Group
                  setSelectedId(absoluteObj.id);
                }}
              />
              <Text
                x={padding}
                y={padding}
                text={absoluteObj.content || ''}
                fontSize={stickyNoteFontSize}
                fill={absoluteObj.textColor || '#000000'}
                width={textWidth}
                height={textHeight}
                align="left"
                verticalAlign="top"
                wrap="word"
                ellipsis
                visible={editingStickyNote?.id !== absoluteObj.id}
                onClick={(e) => {
                  // Allow editing only when select tool OR sticky tool is active
                  if (!canInteract) return;

                  // Prevent editing if we're using any tool other than select or sticky
                  if (!['select', 'sticky'].includes(activeTool)) {
                    // For eraser, let the event bubble so the eraser handler can process it
                    if (activeTool === 'eraser') {
                      return;
                    }

                    // For all other tools, prevent editing
                    e.cancelBubble = true;
                    if (e.evt) {
                      e.evt.stopPropagation();
                    }
                    return;
                  }

                  // Click on text - enter editing mode
                  e.cancelBubble = true; // Prevent event from bubbling to Group
                  bringToFront(absoluteObj.id, (updatedObjects) => {
                    // Emit update to other peers
                    emitUpdateAnnotation(updatedObjects);
                  });
                  setSelectedId(absoluteObj.id);

                  // Enter editing mode
                  const stage = e.target.getStage();
                  if (stage && stage.findOne) {
                    const stickyNoteNode = stage.findOne(`#${absoluteObj.id}`);
                    if (stickyNoteNode && stickyNoteNode.getAbsolutePosition) {
                      const absolutePos = stickyNoteNode.getAbsolutePosition();
                      const x = absolutePos.x;
                      const y = absolutePos.y;
                      const width = rectWidth;
                      const height = rectHeight;

                      setEditingStickyNote({
                        id: absoluteObj.id,
                        x,
                        y,
                        width,
                        height,
                        content: absoluteObj.content || '',
                        fontSize: absoluteObj.fontSize || 14,
                        textColor: absoluteObj.textColor || '#000000',
                      });
                    }
                  }
                }}
              />
            </Group>
          );
        default:
          return null;
      }
    },
    [
      stageRef,
      currentCreatedById,
      selectedId,
      hoveredId,
      activeTool,
      canDeleteOthersObjects,
      canAnnotate,
      isEditingText,
      handleObjectUnhover,
      brushColor,
      editingStickyNote?.id,
      handleObjectClick,
      handleEraserClick,
      handleObjectHover,
      setTooltipPos,
      objects,
      setObjects,
      emitUpdateAnnotation,
      handleTextClick,
      handleTextDblClick,
      setSelectedId,
      bringToFront,
      setEditingStickyNote,
    ]
  );

  return {
    renderObject,
  };
};

export default useObjectRenderer;
